import { GetBookParams } from '@/data/services/bookServices';
import { updateChapter } from '@/data/services/chapterServices';
import {
  createSection,
  updateSectionsOrder,
} from '@/data/services/sectionServices';
import { Section } from '@/models/Book';
import Chapter from '@/models/Chapter';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';
import { useMutation } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import ChapterForm from './ChapterForm';
import Subchapter from '@/models/Subchapter';
import {
  createSubchapter,
  deleteSubchapter,
  updateSubchapter,
} from '@/data/services/subchapterServices';

type ChapterServiceProps = {
  subchapter: Subchapter;
  chapter: Chapter;
  onUpdateChapter: (chapter: Chapter) => void;
  isUniqueSubchapter?: boolean;
} & GetBookParams;

const ChapterService = ({
  chapter,
  onUpdateChapter,
  isUniqueSubchapter,
  subchapter,
  ...bookParams
}: ChapterServiceProps) => {
  const [t] = useTranslation('translation', {
    keyPrefix: 'subchapter',
  });

  const chapterId = chapter.id;

  const chapterParams = {
    ...bookParams,
    chapterId,
  };

  const handleUpdateChapter = async ({
    chapter: chapterChanged,
    validated,
  }: {
    chapter: Partial<Chapter>;
    validated: boolean;
  }) => {
    if (!validated) return;

    const updatedChapter = await updateChapter({
      params: chapterParams,
      body: { layout: chapterChanged.layout },
    });

    onUpdateChapter(updatedChapter);

    return updatedChapter;
  };

  const { mutate: updateChapterLayout } = useMutation(handleUpdateChapter, {
    onError: err => {
      const errMessage = getErrorMessage(err);
      alert.error(errMessage);
    },
  });

  const handleEditSubchapter = async ({
    subchapter: subchapterChanged,
    validated,
  }: {
    subchapter: Partial<Subchapter>;
    validated: boolean;
  }) => {
    if (!validated) return;
    const changes = buildChangedObject(subchapter, subchapterChanged);

    if (subchapter.id) {
      return await updateSubchapter(
        { ...chapterParams, subchapterId: subchapter.id },
        changes,
      );
    } else {
      const newSubchapter = await createSubchapter({
        ...bookParams,
        chapterId: chapterId,
      });

      const newSection = await createSection({
        ...bookParams,
        chapterId: chapterId,
        subchapterId: newSubchapter.id,
      });

      return { ...newSubchapter, sections: [newSection] };
    }
  };

  const { mutate: onEditSubchapter } = useMutation(handleEditSubchapter, {
    onSuccess: subchapter => {
      if (!subchapter) return;

      const updatedChapter = chapter.items?.map(item =>
        item.id === subchapter.id ? subchapter : item,
      );

      onUpdateChapter({ ...chapter, items: updatedChapter });
    },
    onError: error => {
      const errorMessage = getErrorMessage(error);
      alert.error(errorMessage);
    },
  });

  const handleAddSection = async () => {
    const sections = subchapter.sections || [];
    const newSection = await createSection(
      {
        ...bookParams,
        chapterId: chapter.id,
        subchapterId: subchapter.id,
      },
      new Section({ order: sections.length + 1 }),
    );

    sections.push(newSection);

    const updatedSubchapter: Subchapter = {
      ...subchapter,
      sections,
    };

    const subchapters = chapter.items || [];

    const newSubchapters = subchapters.map(subchapter =>
      subchapter.id === updatedSubchapter.id ? updatedSubchapter : subchapter,
    );

    onUpdateChapter({
      ...chapter,
      items: newSubchapters,
    });
  };

  const { mutateAsync: onDeleteSubchapter, isLoading: isDeletingChapter } =
    useMutation(deleteSubchapter, {
      onSuccess: () => {
        const newSubchapters = chapter.items?.filter(
          item => item.id !== subchapter.id,
        );

        onUpdateChapter({ ...chapter, items: newSubchapters });
      },
      onError: error => {
        const errorMessage = getErrorMessage(error);
        alert.error(errorMessage);
      },
    });

  const handleDeleteSubchapter = async () => {
    if (isUniqueSubchapter) {
      alert.warning(t('notDeleteUniqueSubchapter'));
      return;
    }
    await onDeleteSubchapter({
      ...chapterParams,
      subchapterId: subchapter.id,
    });
  };

  const { mutate: onAddSection, isLoading: isAddingSection } = useMutation(
    handleAddSection,
    {
      onError: err => {
        const errMessage = getErrorMessage(err);
        alert.error(errMessage);
      },
    },
  );

  const handleDeleteSection = (deleteSection: Section) => {
    const sections = subchapter.sections || [];

    const newSections = sections.filter(
      section => section.id !== deleteSection.id,
    );

    const updatedSubchapter: Subchapter = {
      ...subchapter,
      sections: newSections,
    };

    const subchapters = chapter.items || [];

    const newSubchapters = subchapters.map(subchapter =>
      subchapter.id === updatedSubchapter.id ? updatedSubchapter : subchapter,
    );

    onUpdateChapter({
      ...chapter,
      items: newSubchapters,
    });
  };

  const handleUpdateSection = (updatedSection: Section) => {
    const sections = subchapter.sections || [];

    const newSections = sections.map(section =>
      section.id === updatedSection.id ? updatedSection : section,
    );

    const updatedSubchapter: Subchapter = {
      ...subchapter,
      sections: newSections,
    };

    const subchapters = chapter.items || [];

    const newSubchapters = subchapters.map(subchapterItem =>
      subchapterItem.id === updatedSubchapter.id
        ? updatedSubchapter
        : subchapterItem,
    );

    onUpdateChapter({
      ...chapter,
      items: newSubchapters,
    });
  };

  const handleOnReorderSections = (sections: Section[]) => {
    updateSectionsOrder({
      params: {
        ...bookParams,
        chapterId: chapter.id,
        subchapterId: subchapter.id,
      },
      sections: sections.map(({ id }) => ({ id })),
    });

    const updatedSubchapter: Subchapter = {
      ...subchapter,
      sections,
    };

    const subchapters = chapter.items || [];

    const newSubchapters = subchapters.map(subchapter =>
      subchapter.id === updatedSubchapter.id ? updatedSubchapter : subchapter,
    );

    onUpdateChapter({ ...chapter, items: newSubchapters });
  };

  return (
    <ChapterForm
      onSubmitSubchapter={debounce(onEditSubchapter, 500)}
      subchapter={subchapter}
      chapter={chapter}
      onSubmitChapter={debounce(updateChapterLayout, 500)}
      onDeleteSection={handleDeleteSection}
      onUpdateSection={handleUpdateSection}
      onAddSection={onAddSection}
      onDeleteSubchapter={handleDeleteSubchapter}
      isDeletingChapter={isDeletingChapter}
      isAddingSection={isAddingSection}
      onReorderSections={handleOnReorderSections}
      {...bookParams}
    />
  );
};

export default ChapterService;
